// Parameter field names, shared by Layout.js (the schema) and the page (formRefs lookups and the
// checkbox cascade). Declared once so a rename cannot silently break the page's imperative field
// access, which is keyed by these exact strings. They are also the request property names the API
// binds, so they match PurgeNeverUsedRecordsDTO.
export const FIELD = Object.freeze({
    PurgeItems: "PurgeItems",
    PurgeLots: "PurgeLots",
    PurgeSublots: "PurgeSublots",
});

/**
 * The purge level the three checkboxes resolve to. Mirrors
 * Argos.Core/Constants/PurgeNeverUsedRecordsConstants.cs -> PurgeLevelCodes.
 *
 * Used ONLY on the client, to pick the filter catalogue and the preview column set — the request sends
 * the three booleans and the server resolves the level itself, so this is never on the wire.
 */
export const PurgeLevel = Object.freeze({
    Item: "I",
    Lot: "L",
    Sublot: "S",
});

/**
 * Resolves the level the same way the server's ResolveLevel and the Delphi MainAction do: Item beats Lot
 * beats Sublot. Kept here rather than inline so the page, the report picker and the column picker cannot
 * disagree about which level is active.
 *
 * With nothing ticked it answers Lot. That state is display-only — neither Preview nor Start will run on
 * it, the client refuses it and so does the server — so the only question is which catalogue to show,
 * and Lot is what the desktop form shows: uWMD0120.dfm points wbPurgeUnusedItems.DataSet at
 * frmWMM0120.qryWM_Lot, which is also this screen's initial state. For every combination that CAN run,
 * the answer is identical to the server's.
 */
export const resolvePurgeLevel = (fields) => {
    if (fields[FIELD.PurgeItems]) {
        return PurgeLevel.Item;
    }

    if (fields[FIELD.PurgeLots]) {
        return PurgeLevel.Lot;
    }

    return fields[FIELD.PurgeSublots] ? PurgeLevel.Sublot : PurgeLevel.Lot;
};

/**
 * Filter-field definitions come from Argos.Core/Report/WM/WMPurgeNeverUsedRecords.cs via ReportFactory's
 * three cases. One catalogue per level, because each level filters on a different target table — a
 * Lot-level filter naming IMSUBLOT.SUBLOTCODE is rejected server-side rather than compiled into a
 * statement that does not join IMSUBLOT.
 */
export const REPORT = Object.freeze({
    [PurgeLevel.Item]: {
        name: "WMPurgeNeverUsedItems",
        fullName: "Argos.Report.WM.WMPurgeNeverUsedItems, Argos.Report.WM",
    },
    [PurgeLevel.Lot]: {
        name: "WMPurgeNeverUsedLots",
        fullName: "Argos.Report.WM.WMPurgeNeverUsedLots, Argos.Report.WM",
    },
    [PurgeLevel.Sublot]: {
        name: "WMPurgeNeverUsedSublots",
        fullName: "Argos.Report.WM.WMPurgeNeverUsedSublots, Argos.Report.WM",
    },
});

/**
 * Saved-layout identity for the Preview grid — one key per purge level.
 *
 * The three levels declare different column sets (5 / 6 / 7), and StateStoring persists the whole
 * column array: order, widths, visibility. Under a shared key, reordering or removing columns at Sublot
 * level would save seven columns' worth of state and then restore it against Item level's five-column
 * declaration — leaving LotCode/SublotCode to come back as permanently empty columns, in an order Item
 * level never defined. Separate keys keep each level's layout coherent with the columns it actually has.
 *
 * This was briefly collapsed to a single key, because a per-level key fired GetGridLayouts on every
 * checkbox click. That is no longer true and the collapse was the wrong fix: the popup is now gated on
 * `previewMounted`, and every level change unmounts the grid before the key can change. So the key is
 * only ever read when the grid mounts — i.e. when Preview is pressed — and a checkbox click fires no
 * layout call at all, whichever scheme is used. Three keys therefore cost nothing and buy correctness.
 */
export const PREVIEW_GRID_KEY = Object.freeze({
    [PurgeLevel.Item]: "2C7F4E19-8B03-4D56-A1E4-97D05B3C6182",
    [PurgeLevel.Lot]: "6E1A5D82-3C47-4F90-B85D-1274A9E0C536",
    [PurgeLevel.Sublot]: "9D34B70E-5F62-41A8-8C17-4E60D2951BA7",
});

// GridLayoutManager's built-in layout name, used as the initial currentLayout state.
export const DEFAULT_LAYOUT_NAME = "Default";

// Dynamic-form layout identity for the parameters panel.
export const LAYOUT_ID = "8A5C31F7-4D96-42B0-9E28-13B7C6540FDA";
export const LAYOUT_NAME = "WMPurgeNeverUsedRecordsForm";

// The form's lblTitle (uWMD0120.dfm), with Delphi's escaped && collapsed to one ampersand. The window
// caption is "Purge Unused Items, Lots & Sublot Master Records (WMD0120)"; the title band is the one
// users read, so it is the one reproduced.
export const PAGE_TITLE = "Purge Never Used Items, Lots and Sublots";

// The read-only criteria memo (uWMD0120.dfm edtMemo), verbatim apart from collapsing the Delphi line
// wrapping. It is the only place the screen explains which records qualify, so it is worth reproducing
// exactly rather than paraphrasing.
export const HELP_TEXT =
    "Warning : This Program will PURGE Item, Lot and Sublot Master Records Based on the following "
    + "criteria.\n"
    + "(1) Transactions do NOT exist for the selected records (ie No WM Transaction Details or Sub "
    + "Details, IM Transaction Locations).\n"
    + "(2) Quantity status information must be zero in all fields for the selected records.";

// The second memo (edtBackMemo), rendered in red bold on the Delphi form. Kept as its own band rather
// than appended to HELP_TEXT because it is a different kind of statement — a precondition for using the
// screen at all, not a description of what it does.
export const BACKUP_WARNING_TEXT =
    "It is Strongly Recommended that You have a Very Good Backup Of the current Abecas Insight database "
    + "before proceeding.";

// The group box caption above the three checkboxes (hdrBottomLeft), verbatim.
export const SELECTION_TITLE = "Purge Records From Following Master Tables";

// Checkbox captions, verbatim from the DFM.
export const CHECKBOX_LABEL = Object.freeze({
    PurgeItems: "Purge Item Records",
    PurgeLots: "Purge Lot Records",
    PurgeSublots: "Purge Sublot Records",
});

export const PREVIEW_TITLE = "Preview";

// Toolbar button captions, verbatim from the Delphi form (uWMD0120.dfm grpBottomButtons), in its
// left-to-right order. Help and Exit are not migrated: Help opened a Delphi context topic and Exit
// closed the MDI child.
export const BUTTON = Object.freeze({
    Start: "Start",
    Preview: "Preview",
    Reset: "Reset",
});

// The Delphi allowed the preview queries 900s (uWMD0120.pas:128-130) and the DELETE is heavier still.
export const REQUEST_TIMEOUT_MS = 20 * 60 * 1000;

/**
 * Deadline for the GetReport that loads a level's filter catalogue.
 *
 * Needed rather than merely defensive: CommonReport.loadReport swallows a failure into console.log and
 * never calls onReportStateChange (CommonReport.js:555), so on a failed fetch nothing else would ever
 * lower the load panel. Short — this is a small metadata fetch, not the 20-minute purge — but generous
 * enough that a slow-but-working server is not cut off mid-flight.
 */
export const REPORT_LOAD_TIMEOUT_MS = 30 * 1000;
